import React, { useState, useEffect, useContext, createContext } from 'react'
import Amplify, { Auth, Hub } from 'aws-amplify'
import Token from '../apis/Token'

Amplify.configure({
    Auth: {
        region: process.env.REACT_APP_REGION,
        userPoolId: process.env.REACT_APP_USER_POOL_ID,
        userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID,
    },
})
const authContext = createContext({})

export function ProvideAuth({ children }) {
    const auth = useProvideAuth()
    return <authContext.Provider value={auth}>{children}</authContext.Provider>
}

export const useAuth = () => {
    return useContext(authContext)
}

function useProvideAuth() {
    const [user, setUser] = useState(null)
    const [isInstaller, setIsInstaller] = useState(null)
    const [isAdminOrManager, setIsAdminOrManager] = useState(null)
    const [token, setToken] = useState(null)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [userRole, setUserRole] = useState(null)
    const [userId, setUserId] = useState(null);

    const ROLES = {
        CONSUMER: 'CONSUMER',
        CONSUMER_GUEST: 'CONSUMER_GUEST',
        INSTALLER: 'INSTALLER',
        ADMIN: 'ADMIN',
        PROPERTY_MANAGER: 'PROPERTY_MANAGER'
    }

    const isConsumer = () => userRole === ROLES.CONSUMER
    const isConsumerGuest = () => userRole === ROLES.CONSUMER_GUEST
    const isConsumerOrGuest = () => isConsumer() || isConsumerGuest()

    const getCognitoSub = (u) =>
        u?.attributes?.sub ??
        u?.signInUserSession?.idToken?.payload?.sub ??
        u?.signInUserSession?.accessToken?.payload?.sub ??
        null;

    // The persona a user acts as in THIS app. Roles are additive (an admin may also be a
    // consumer, granted on their first sign-in here), so the personas this app serves come
    // first, in their existing order, and ADMIN / PROPERTY_MANAGER decide only when the
    // user holds none of them — the case Login bounces to the admin or manager app.
    const extractUserRole = (user) => {
        let userRoles = JSON.parse(
            user?.signInUserSession?.idToken?.payload?.roles || '[]'
        )

        // Ensure userRoles is always an array (handle case where JSON.parse returns null)
        if (!Array.isArray(userRoles)) {
            userRoles = []
        }

        if (userRoles.includes(ROLES.INSTALLER)) {
            return ROLES.INSTALLER
        } else if (userRoles.includes(ROLES.CONSUMER_GUEST)) {
            return ROLES.CONSUMER_GUEST
        } else if (userRoles.includes(ROLES.CONSUMER)) {
            return ROLES.CONSUMER
        } else if (userRoles.includes(ROLES.ADMIN)) {
            return ROLES.ADMIN
        } else if (userRoles.includes(ROLES.PROPERTY_MANAGER)) {
            return ROLES.PROPERTY_MANAGER
        }

        return ROLES.CONSUMER
    }

    // Whether the user holds a persona this app serves; without one an ADMIN or
    // PROPERTY_MANAGER belongs in their own app.
    const hasConsumerAppPersona = (user) => {
        const role = extractUserRole(user)
        return role !== ROLES.ADMIN && role !== ROLES.PROPERTY_MANAGER
    }

    const signin = (email, password, validationData) => {
        return Auth.signIn({
            username: email,
            password: password,
            validationData: validationData,
        }).then((response) => {
            Token.setToken(response.signInUserSession.idToken.jwtToken)
            setToken(response.signInUserSession.idToken.jwtToken)

            setUserId(getCognitoSub(response));
            const role = extractUserRole(response)
            setUserRole(role)
            
            isUserInstaller(response)
            isUserAdminOrManager(response)
            setUser(response)
            setIsAuthenticated(true)
            
            return response
        })
    }

    const signout = () => {
        return Auth.signOut().then(() => {
            setUser(null)
            setToken(null)
            setIsInstaller(null)
            setIsAdminOrManager(null)
            setIsAuthenticated(false)
            setUserRole(null)
        })
    }

    const isUserLoggedIn = () => {
        return Auth.currentAuthenticatedUser().then((user) => {
            Token.setToken(user.signInUserSession.idToken.jwtToken)
            setToken(user.signInUserSession.idToken.jwtToken)

            setUserId(getCognitoSub(user));
            const role = extractUserRole(user)
            setUserRole(role)
            
            isUserInstaller(user)
            isUserAdminOrManager(user)
            setUser(user)
            setIsAuthenticated(true)
            
            return user
        })
    }

    const isUserInstaller = (user) => {
        let userRoles = JSON.parse(
            user?.signInUserSession?.idToken?.payload?.roles || '[]'
        )
        // Ensure userRoles is always an array (handle case where JSON.parse returns null)
        if (!Array.isArray(userRoles)) {
            userRoles = []
        }
        let isInstaller = userRoles.includes('INSTALLER')
        setIsInstaller(isInstaller)
        return isInstaller
    }

    const isUserAdminOrManager = (user) => {
        let userRoles = JSON.parse(
            user?.signInUserSession?.idToken?.payload?.roles || '[]'
        )
        // Ensure userRoles is always an array (handle case where JSON.parse returns null)
        if (!Array.isArray(userRoles)) {
            userRoles = []
        }
        let isUserAdminOrManager = userRoles.includes('ADMIN') || userRoles.includes('PROPERTY_MANAGER')
        let isUserAdmin = userRoles.includes('ADMIN')
        let isUserManager = userRoles.includes('PROPERTY_MANAGER')
        setIsAdminOrManager(isUserAdminOrManager)
        return { isUserAdminOrManager, isUserAdmin, isUserManager }
    }

    useEffect(() => {
        const unsubscribe = Hub.listen('auth', (data) => {
            switch (data.payload.event) {
                case 'signIn':
                    const role = extractUserRole(data.payload.data)
                    setUserRole(role)
                    setUser(data.payload.data)
                    isUserInstaller(data.payload.data)
                    isUserAdminOrManager(data.payload.data)
                    setIsAuthenticated(true)
                    // Update token if available
                    if (data.payload.data?.signInUserSession?.idToken?.jwtToken) {
                        Token.setToken(data.payload.data.signInUserSession.idToken.jwtToken)
                        setToken(data.payload.data.signInUserSession.idToken.jwtToken)
                    }
                    break
                case 'signOut':
                    setUser(false)
                    setUserRole(null)
                    setIsAuthenticated(false)
                    break
                default:
                    break
            }
        })

        return () => unsubscribe()
    }, [])

    return {
        user,
        isInstaller,
        isAdminOrManager,
        isAuthenticated,
        token,
        userId,
        userRole,
        ROLES,
        isConsumer,
        isConsumerGuest,
        isConsumerOrGuest,
        signin,
        isUserInstaller,
        isUserAdminOrManager,
        hasConsumerAppPersona,
        signout,
        isUserLoggedIn,
    }
}
